import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  NIVEL_PAPEL,
  type EntradaLogin,
  type Papel,
  type Permissao,
  type ReferenciaOrganizacional,
  type UsuarioSessao,
} from '@baiocchi/shared'

import { aoPerderSessao, definirTokenAcesso } from '../api/cliente'
import { autenticacaoApi } from '../api/autenticacao'

export interface EscopoSessao {
  readonly unidades: ReadonlyArray<ReferenciaOrganizacional>
  readonly equipes: ReadonlyArray<ReferenciaOrganizacional>
  readonly irrestrito: boolean
}

interface EstadoSessao {
  usuario: UsuarioSessao | null
  permissoes: ReadonlyArray<Permissao>
  escopo: EscopoSessao | null
  /** `true` só até a primeira tentativa de restaurar a sessão terminar. */
  carregando: boolean
}

interface ContextoSessao extends EstadoSessao {
  autenticado: boolean
  entrar: (entrada: EntradaLogin) => Promise<UsuarioSessao>
  sair: () => Promise<void>
  /** Recarrega usuário, permissões e escopo (após trocar equipe, papel, foto). */
  recarregar: () => Promise<void>
  /** Aplica o par novo devolvido por `POST /auth/alterar-senha`. */
  aplicarSessao: (dados: {
    usuario: UsuarioSessao
    permissoes: ReadonlyArray<Permissao>
  }) => void
  pode: (...permissoes: ReadonlyArray<Permissao>) => boolean
  podeQualquer: (...permissoes: ReadonlyArray<Permissao>) => boolean
  /** Nível de papel ≥ o informado — para as regras que a matriz não cobre (D4). */
  peloMenos: (papel: Papel) => boolean
}

const Contexto = createContext<ContextoSessao | null>(null)

/**
 * Sessão do usuário no cliente.
 *
 * **A lista de permissões serve para esconder e desabilitar elemento, e nada
 * mais** (`docs/security/03 §5`). Esconder não é autorizar: o backend nega de todo
 * modo, e é por isso que existe `EstadoSemPermissao` — a tela precisa lidar com o
 * `403` que chega mesmo com o botão escondido.
 *
 * A restauração na abertura da aba passa por `POST /auth/refresh`, que devolve
 * usuário, permissões e token juntos. Guardar o usuário em `localStorage` para
 * "abrir mais rápido" seria confiar num dado que pode estar velho: papel, equipe
 * ou status alterado revoga as sessões (RN-73), e a tela mostraria menus que o
 * servidor já não aceita.
 */
export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSessao>({
    usuario: null,
    permissoes: [],
    escopo: null,
    carregando: true,
  })
  const clienteConsulta = useQueryClient()

  const limpar = useCallback(() => {
    definirTokenAcesso(null)
    setEstado({ usuario: null, permissoes: [], escopo: null, carregando: false })
    clienteConsulta.clear()
  }, [clienteConsulta])

  // Refresh que falhou em definitivo: o cliente HTTP avisa e a sessão cai aqui,
  // em um lugar só, em vez de cada tela tratar 401.
  useEffect(() => aoPerderSessao(limpar), [limpar])

  const carregarEscopo = useCallback(async () => {
    const eu = await autenticacaoApi.eu()
    setEstado({
      usuario: eu.usuario,
      permissoes: eu.permissoes,
      escopo: eu.escopo,
      carregando: false,
    })
  }, [])

  useEffect(() => {
    let cancelado = false

    void (async () => {
      const restaurada = await autenticacaoApi.restaurar()
      if (cancelado) return

      if (!restaurada) {
        setEstado({ usuario: null, permissoes: [], escopo: null, carregando: false })
        return
      }

      // Já temos usuário e permissões do refresh; `/auth/eu` acrescenta o escopo
      // organizacional, que os seletores das telas de filtro usam.
      setEstado({
        usuario: restaurada.usuario,
        permissoes: restaurada.permissoes,
        escopo: null,
        carregando: false,
      })
      try {
        await carregarEscopo()
      } catch {
        /* Escopo é complemento: sem ele a tela funciona com o que veio do login. */
      }
    })()

    return () => {
      cancelado = true
    }
  }, [carregarEscopo])

  const entrar = useCallback(
    async (entrada: EntradaLogin) => {
      const resposta = await autenticacaoApi.login(entrada)
      setEstado({
        usuario: resposta.usuario,
        permissoes: resposta.permissoes,
        escopo: null,
        carregando: false,
      })
      try {
        await carregarEscopo()
      } catch {
        /* idem */
      }
      return resposta.usuario
    },
    [carregarEscopo],
  )

  const sair = useCallback(async () => {
    await autenticacaoApi.sair()
    limpar()
  }, [limpar])

  const valor = useMemo<ContextoSessao>(() => {
    const conjunto = new Set<string>(estado.permissoes)
    const nivel = estado.usuario ? NIVEL_PAPEL[estado.usuario.papel] : 0

    return {
      ...estado,
      autenticado: estado.usuario !== null,
      entrar,
      sair,
      recarregar: carregarEscopo,
      aplicarSessao: (dados) =>
        setEstado((atual) => ({
          ...atual,
          usuario: dados.usuario,
          permissoes: dados.permissoes,
          carregando: false,
        })),
      pode: (...permissoes) => permissoes.every((permissao) => conjunto.has(permissao)),
      podeQualquer: (...permissoes) => permissoes.some((permissao) => conjunto.has(permissao)),
      peloMenos: (papel) => nivel >= NIVEL_PAPEL[papel],
    }
  }, [estado, entrar, sair, carregarEscopo])

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto)
  if (!contexto) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>.')
  return contexto
}

/** Atalho para as telas que só querem saber se podem mostrar um botão. */
export function usePermissao(...permissoes: ReadonlyArray<Permissao>): boolean {
  return useSessao().pode(...permissoes)
}
